import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AccessLevel } from '@/__generated__/graphql';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { allSearchTypes, useFilterContext } from '@/context/FilterProvider';
import { useSearchResults } from '@/hooks/discovery/appliedSearch';
import { useValidatedParams } from '@/hooks/params';
import { isDevelopment } from '@/typings/url';
import { truthy } from '@/util/array';
import { MAXIMUM_QUERY_LENGTH, QUERY_TOO_LONG_MESSAGE } from '@/util/input';
import { toSearchResourceType } from '@/util/resourceType';
import { INITIAL_PAGE_SIZE } from '../../resource/components/ResourceFilterTable';
import { SearchResults, SearchResultsParams } from './SearchResultsList';

export const usePageTitleQuery = (): string => {
  const [searchParams] = useSearchParams();
  return searchParams.get('search') || '';
};

export const SearchResultsContents = () => {
  const { filters } = useFilterContext();

  const pageSize = INITIAL_PAGE_SIZE;
  const [searchParams] = useSearchParams();

  /**
   * The actual query is the search term we want to filter by, ignoring all
   * selectors.
   */
  const searchInput = searchParams.get('search') || '';
  const isQueryTooLong = searchInput.length > MAXIMUM_QUERY_LENGTH;

  const { environmentType } = useValidatedParams();
  const { environmentId, userId } = useDbtCloudProject();
  const searchQuery = useSearchResults({
    environmentId,
    userId: isDevelopment(environmentType) ? userId : -1,
    filter: {
      query: searchInput,
      fields: filters.searchFields?.length ? filters.searchFields : allSearchTypes,
      resourceTypes:
        filters.resourceType?.map(toSearchResourceType).filter(truthy) || [],
      tags: filters.tags || [],
      materializationTypes: filters.materialization?.length
        ? filters.materialization
        : null,
      modelingLayers: filters.modelingLayer?.length ? filters.modelingLayer : null,
      accessLevels: filters.access?.length ? (filters.access as AccessLevel[]) : null,
    },
    pageSize: 25,
    pause: isQueryTooLong,
  });

  const title = usePageTitleQuery();

  const resultsProps = useMemo((): SearchResultsParams | null => {
    const pageInfo = {
      hasMoreResults:
        (searchQuery as any)?.hasMoreResults ||
        (searchQuery as any)?.pageInfo?.hasNextPage,
      totalCount:
        (searchQuery as any)?.totalCount || (searchQuery as any).pageInfo?.totalCount,
    };
    return {
      ...searchQuery,
      ...pageInfo,
      pageSize,
      query: searchInput,
      title,
      skeleton: searchQuery.isInitialLoad,
    };
  }, [searchQuery, pageSize, searchInput, title]);

  if (isQueryTooLong) {
    return <div className="p-4 text-sm text-fgDanger">{QUERY_TOO_LONG_MESSAGE}</div>;
  }

  return (
    <div>
      {resultsProps && (
        <SearchResults {...resultsProps} title={resultsProps?.title || ''} />
      )}
    </div>
  );
};
